import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FmAutocompleteInput, FmButton, FmDatePicker, FmRadioButton, FmTemperatureCard } from "../../../Components";
import { useIsMobile } from "../../../Hooks";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { LOGGED_IN_ROUTES, NUTRIENT_RADIO_OPTIONS, TEMPERATURE_MAX, TEMPERATURE_MIN, DEFAULT_TEMPERATURE } from "../../../Constants";
import { CalculateUtils, TimeUtils, ValidationUtils } from "../../../Utils";
import { useDispatch, useSelector } from "react-redux";
import { GeneralState, SolutionState } from "../../../Redux";

import useStyles from "./StepOnePage.style";
import { useHistory } from "react-router-dom";
import { createDate, getDateFormatted, getMonthNumberFromDate, getMonthFromNumber } from "../../../Utils/TimeUtils";
import { MulticoteBlend } from "../../../Models/ServerModels/AgriSolution";
import { MonthTemperature } from "../../../Models";
import { TWO, ZERO, ONE } from "../../../Utils/Consts";

interface IFormInputs {
    selectedBlendObject: MulticoteBlend | undefined;
    plantingDate: string;
    selectedNutrient: string;
}

const StepOnePage = () => {
    const itemId = useSelector(SolutionState.Selectors.selectItemId);
    const [showTempCards, setShowTempCards] = useState<boolean>(!!itemId);
    const { activeMetricMethod } = useSelector(GeneralState.Selectors.selectMetricMethods);

    const selectedBlendObject = useSelector(SolutionState.Selectors.selectBlendObject);
    const selectedNutrient = useSelector(SolutionState.Selectors.selectNutrientType);
    const userEditAfterGettingResult = useSelector(SolutionState.Selectors.selectUserEditAfterGettingResult);

    const [months, setMonths] = useState<
        {
            label: string;
            temperature: number;
            date: string;
        }[]
    >([]);

    const [key, setKey] = useState<number>(0);

    const classes = useStyles();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const history = useHistory();
    const plantingDate = useSelector(SolutionState.Selectors.selectPlantingDate);
    const { temperatures } = useSelector(SolutionState.Selectors.selectTemperatureState);
    const calculateSolutionResultInProgress = useSelector(SolutionState.Selectors.selectCalculateSolutionInProgress);
    const [lastKnowActiveMethod, setLastKnownActiveMethod] = useState(activeMetricMethod);
    // Reset temperature value on measurement units update
    useEffect(() => {
        if (lastKnowActiveMethod !== activeMetricMethod) {
            const newMonths = months.map((item) => ({
                ...item,
                temperature:
                    activeMetricMethod === "1" ? CalculateUtils.fahrenheitToCelsius(item.temperature) : CalculateUtils.celsiusToFahrenheit(item.temperature),
            }));
            setMonths(newMonths);
            setLastKnownActiveMethod(activeMetricMethod);
            const temperatures = newMonths.map((month, index) => ({
                monthNumber: month.date.length > 1 ? getMonthNumberFromDate(month.date) : Number(month.date),
                tempatureValue: month.temperature,
                index: index,
            }));

            dispatch(
                SolutionState.Actions.setTemperatureState({
                    temperatures,
                    selectedTemperatureUnit: activeMetricMethod,
                })
            );
        }
    }, [activeMetricMethod, lastKnowActiveMethod]);

    useEffect(() => {
        (window as any).dataLayer.push({
            event: "MulticoteTemperaturesStage",
            pageUrl: "https://multimatch.haifa-group.com/multi-match/solution/temperature",
            pageTitle: "MulticoteTemperaturesStage",
        });
    }, []);

    const {
        handleSubmit,
        getValues,
        setValue,
        control,
        register,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: "onBlur",
        defaultValues: {
            plantingDate,
            selectedBlendObject,
            selectedNutrient,
        },
    });
    const plantingDateDefaultValue = TimeUtils.getDateFormatted(TimeUtils.getDate(0));
    useEffect(() => {
        const getAllBlendsHandler = async () => {
            await dispatch(SolutionState.Thunks.getAllMulticoteBlends());
        };

        getAllBlendsHandler();
        if (plantingDate == undefined) {
            setValue("plantingDate", plantingDateDefaultValue);
            dispatch(SolutionState.Actions.setSelectedPlantingDate(plantingDateDefaultValue));
        }

        temperatures.map((mon: MonthTemperature) => {
            setMonths([]);
            months.push({
                label: getMonthFromNumber(mon.monthNumber),
                temperature: mon.tempatureValue,
                date: mon.monthNumber.toString(),
            });
        });
        setMonths(months);
    }, []);

    const AllMulticoteBlends = useSelector(SolutionState.Selectors.selectAllMulticoteBlends);

    const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
        if (showTempCards) {
            const monthsToSend: MonthTemperature[] = [];
            months.map((mon, index) => {
                monthsToSend.push({
                    monthNumber: mon.date.length > 1 ? getMonthNumberFromDate(mon.date) : Number(mon.date),
                    tempatureValue: mon.temperature,
                    index: index,
                });
            });

            dispatch(
                SolutionState.Actions.setTemperatureState({
                    temperatures: monthsToSend,
                    selectedTemperatureUnit: activeMetricMethod,
                })
            );
            dispatch(SolutionState.Actions.setSelectedPlantingDate(plantingDate));
            await dispatch(
                SolutionState.Thunks.calculateMulticoteSolutionsResult(() => {
                    history.push(LOGGED_IN_ROUTES.SOLUTION_SECOND_STEP);
                })
            );
        }
        setTemperatures();

        setShowTempCards(true);
    };
    useEffect(() => {
        if (plantingDate === undefined) {
            setValue("plantingDate", plantingDateDefaultValue);
        }

        setTemperatures();
    }, [plantingDate]);

    useEffect(() => {
        setTemperatures();
    }, [selectedBlendObject]);

    const setTemperatures = () => {
        const startDate: string = plantingDate ? plantingDate : plantingDateDefaultValue;
        const long = selectedBlendObject && selectedBlendObject.longevity ? selectedBlendObject.longevity : ZERO;
        const fromDateParsed = createDate(startDate);

        const finishDate = getDateFormatted(fromDateParsed.add(long - 1, "month"));

        const datesRange = TimeUtils.getDatesRange(startDate, finishDate);

        const defaultValue = (DEFAULT_TEMPERATURE as any)[activeMetricMethod as string];
        const monthsCard = datesRange.map((date) => ({
            date: date,
            value: defaultValue,
        }));

        if (userEditAfterGettingResult) {
            setMonths(
                temperatures.map((mon: MonthTemperature) => {
                    return {
                        label: getMonthFromNumber(mon.monthNumber),
                        temperature: mon.tempatureValue,
                        date: mon.monthNumber.toString(),
                    };
                })
            );
            //   setMonths(months);
        } else {
            setMonths(
                monthsCard.map((temp) => {
                    const date = TimeUtils.createDate(temp.date);
                    // Zero based
                    const month = date.get("month") + 1;
                    return {
                        label: t(`months.${month}`),
                        date: temp.date,
                        temperature:
                            lastKnowActiveMethod == activeMetricMethod
                                ? temp.value
                                : activeMetricMethod === "1"
                                ? CalculateUtils.fahrenheitToCelsius(temp.value)
                                : CalculateUtils.celsiusToFahrenheit(temp.value),
                    };
                })
            );
        }
    };

    const updateMonthsValue = (index: number, newValue: number) => {
        if (newValue <= (TEMPERATURE_MAX as any)[activeMetricMethod] && newValue >= (TEMPERATURE_MIN as any)[activeMetricMethod]) {
            const newMonths = [...months];
            newMonths[index].temperature = newValue;
            setMonths(newMonths);
            const temperatures = newMonths.map((item, index) => ({
                monthNumber: TimeUtils.createDate(item.date.toString()).get("month") + 1,
                tempatureValue: item.temperature,
                index: index,
            }));
            const selectedTemperatureUnit: string = activeMetricMethod;

            dispatch(
                SolutionState.Actions.setTemperatureState({
                    temperatures,
                    selectedTemperatureUnit,
                })
            );
        }
    };
    return (
        <form onSubmit={handleSubmit(handleOnSubmit)} className={isMobile ? classes.mobile_stepOne_container : classes.stepOne_container}>
            <div className={isMobile ? classes.mobile_title_container_stepOne : classes.title_container_stepOne}>{t("multicoteSolutionPage.title")}</div>
            <div className={isMobile ? classes.mobile_stepOne_firstRow : classes.stepOne_firstRow}>
                <div className={isMobile ? classes.mobile_stepOne_inputs_container : classes.stepOne_inputs_container}>
                    <div className={isMobile ? classes.mobile_inputWrapper : classes.inputWrapper}>
                        <Controller
                            name={"selectedBlendObject"}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t("validationErrors.fieldIsRequired"),
                                },
                            }}
                            render={({ field }) => {
                                return (
                                    <FmAutocompleteInput
                                        {...register("selectedBlendObject")}
                                        {...field}
                                        label={t("multicoteSolutionPage.stepOne.firstInput")}
                                        error={errors.selectedBlendObject}
                                        helperText={errors.selectedBlendObject ? t("validationErrors.fieldIsRequired") : ""}
                                        data={AllMulticoteBlends}
                                        getLabel={(blend: any) => blend.name}
                                        setValue={(blend: any) => {
                                            setValue("selectedBlendObject", blend && blend ? blend : undefined);
                                            dispatch(SolutionState.Actions.setUserEditAfterGettingResult(false));

                                            dispatch(SolutionState.Actions.setSelectedBlendObject(blend));
                                        }}
                                    />
                                );
                            }}
                        />
                    </div>
                    <div className={isMobile ? classes.mobile_inputWrapper : classes.inputWrapper}>
                        <Controller
                            name={"plantingDate"}
                            key={`plantingDate-${key}`}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t("validationErrors.fieldIsRequired"),
                                },
                                validate: (value: string) => {
                                    const isValid = ValidationUtils.validateDate(value);
                                    return (!isValid ? t("validationErrors.invalidDateFormat") : undefined) as any;
                                },
                            }}
                            render={({ field }) => {
                                return (
                                    <FmDatePicker
                                        {...register("plantingDate")}
                                        {...field}
                                        label={t("agriSolution.plantingDateLabel")}
                                        variant='filled'
                                        error={errors.plantingDate}
                                        helperText={errors.plantingDate?.message || ""}
                                        defaultValue={plantingDateDefaultValue}
                                        value={field.value}
                                        setValue={(newDate: any) => {
                                            setValue("plantingDate", newDate, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            });
                                            dispatch(SolutionState.Actions.setUserEditAfterGettingResult(false));
                                            dispatch(SolutionState.Actions.setSelectedPlantingDate(newDate));
                                        }}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
                <div className={classes.stepOne_radioButtons_container}>
                    <div className={isMobile ? classes.mobile_inputWrapper : classes.inputWrapper}>
                        <Controller
                            name={"selectedNutrient"}
                            control={control}
                            render={({ field }) => {
                                return (
                                    <FmRadioButton
                                        {...register("selectedNutrient")}
                                        {...field}
                                        noMarginStart={true}
                                        values={NUTRIENT_RADIO_OPTIONS}
                                        getDataLabel={(v) => v}
                                        getDataValue={(v) => v}
                                        defaultValueIndex={NUTRIENT_RADIO_OPTIONS.findIndex((i) => i == selectedNutrient)}
                                        onChange={(e: any) => {
                                            setValue("selectedNutrient", e.target.value, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            });
                                            dispatch(SolutionState.Actions.setSelectedNutrient(e.target.value));
                                        }}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
            </div>
            {showTempCards && <span className={classes.selectTempLabel}>{t("multicoteSolutionPage.stepOne.selectTemperature")}</span>}
            {showTempCards && (
                <div className={classes.tempArray}>
                    {months.map((item: any, index: number) => (
                        <FmTemperatureCard
                            key={index}
                            month={item.label}
                            temperature={item.temperature}
                            setTemperature={(newValue: number) => updateMonthsValue(index, newValue)}
                        />
                    ))}
                </div>
            )}
            <div className={classes.calculateButton_container}>
                <FmButton
                    disabled={calculateSolutionResultInProgress}
                    loading={calculateSolutionResultInProgress}
                    fullWidth={isMobile ? "fullWidth" : "regular"}
                    type='submit'
                >
                    {showTempCards ? t("multicoteSolutionPage.stepOne.calculateButton") : t("multicoteSolutionPage.stepOne.setTempratureButton")}
                </FmButton>
            </div>
        </form>
    );
};

export default StepOnePage;
